import { GlobalStateController } from "bai-react-global-state";
import firebase from "firebase/compat/app";
import React, { Fragment } from "react";

import { AppView, Notification } from "common/constants";
import { IUserStorageModel, IWorkspaceMetadata, WorkspacePlanTier, WorkspacePlanType } from "common/interfaces";
import Analytics from "js/analytics";
import PresentationLibraryPaneController, { PresentationLibraryFilter, PresentationLibraryFilterType } from "js/controllers/PresentationLibraryPaneController";
import WorkspaceController, { createWorkspace, getInitialWorkspaceId, setPersistentWorkspaceId } from "js/controllers/WorkspaceController";
import getLogger, { LogGroup } from "js/core/logger";
import { ds } from "js/core/models/dataService";
import { User } from "js/core/models/user";
import NotificationsService from "js/core/services/notifications";
import { ShowPlayerLoadingScreen } from "js/core/SplashScreen";
import isConnected from "js/core/utilities/isConnected";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { initializeForceReloadDialog } from "js/editor/forceReload";
import PresentationEditorController from "js/editor/PresentationEditor/PresentationEditorController";
import { t } from "js/i18n";
import { app } from "js/namespaces";
import { ShowDialog, ShowErrorDialog, ShowInputDialog, ShowMessageDialog } from "js/react/components/Dialogs/BaseDialog";
import MandatoryUpdateDialog from "js/react/components/Dialogs/MandatoryUpdateDialog";
import { ShowNotificationAlert } from "js/react/components/NotificationAlert";
import { browserHistory } from "js/react/history";
import MemberAddedToWorkspaceDialog from "js/react/views/UserOptions/dialogs/MemberAddedToWorkspaceDialog";
import { _ } from "js/vendor";

const logger = getLogger(LogGroup.CONTROLLERS);
const { EventType, EventAction } = Notification;

// Preloads needed for the editor
import "js/editor/preload";

declare global {
    interface Window {
        roomID: string;
        baiSource: string;
    }
}

export interface AppControllerState {
    isConnected: boolean;
    isConstrained: boolean;
    view: typeof AppView[keyof typeof AppView];
    externalActivePanel: string;
    user: IUserStorageModel;
    userAvatarUrl: string;
    isAccountVerified: boolean;
    workspaceId: string;
    firebaseUser: firebase.User;
}

const initialState: AppControllerState = {
    // App is running in a iframe
    isConstrained: false,
    // Browser is connected to the internet
    isConnected: true,
    workspaceId: null,
    userAvatarUrl: null,
    view: null,
    user: null,
    isAccountVerified: true,
    externalActivePanel: null,
    firebaseUser: null
};

class AppController extends GlobalStateController<AppControllerState> {
    private isSubscribedToConnectionChange: boolean = false;
    public notificationsService: NotificationsService = null;
    public initialized: boolean = false;

    private _handleWorkspacesMetadataUpdated = () => {
        this._updateState({}).catch(err => {
            logger.error(err, "[AppController] _handleWorkspacesMetadataUpdated() failed to update state");
        });
    };

    get orgId() {
        return this._state.workspaceId;
    }

    get workspaceId() {
        return this._state.workspaceId;
    }

    get view() {
        return this._state.view;
    }

    get externalActivePanel() {
        return this._state.externalActivePanel;
    }

    get userAvatarUrl() {
        return this._state.userAvatarUrl;
    }

    get ds() {
        return ds;
    }

    get firebaseUser() {
        return this._state.firebaseUser;
    }

    get user() {
        return this._state.user;
    }

    get workspaceIsPastDue() {
        return WorkspaceController.isPastDue;
    }

    get workspacesMetadata() {
        return (this._state.user?.workspacesMetadata ?? []) as IWorkspaceMetadata[];
    }

    get workspaceMetadata() {
        return this.workspacesMetadata.find(workspace => workspace.id === this.workspaceId) as IWorkspaceMetadata;
    }

    initializePromise = Promise.resolve();

    initialize(firebaseUser, initialWorkspaceId?: string) {
        return new Promise((resolve, reject) => {
            this.initializePromise = this.initializePromise
                .then(async () => {
                    if (!window.roomID) {
                        window.roomID = new URLSearchParams(window.location.search).get("roomID");
                    }

                    // Force set initial state but keep the view
                    await this._updateState(() => ({ ...initialState, view: this.view }));

                    await this.initializeUser(firebaseUser);

                    await this.setInitialWorkspaceId(initialWorkspaceId);

                    await this._updateState({
                        isConstrained: app.isConstrained,
                        isConnected: isConnected.connected,
                        userAvatarUrl: this.user.getAvatarUrl(),
                    });

                    this.initializeNotificationsService();

                    // poll for forced reload
                    initializeForceReloadDialog();

                    if (!this.isSubscribedToConnectionChange) {
                        this.isSubscribedToConnectionChange = true;

                        // update the state if we're offline
                        isConnected.on("change", () => {
                            this._updateState({ isConnected: isConnected.connected });
                        });
                    }

                    // if user came from signup, signup is now complete,
                    // but first wait for analytics events to fire
                    setTimeout(() => {
                        if (Analytics.util.getState("is_signing_up")) {
                            Analytics.util.trackState({
                                is_signing_up: false
                            });
                        }
                    }, 2000);

                    this.initialized = true;
                })
                .then(resolve)
                .catch(reject);
        });
    }

    async reset() {
        logger.info("[AppController] reset");
        this.initialized = false;

        if (this.notificationsService) {
            this.notificationsService.dispose();
        }

        if (this.user) {
            this.user.off();
        }
        if (this.isSubscribedToConnectionChange) {
            isConnected.off("change");
            this.isSubscribedToConnectionChange = false;
        }

        PresentationLibraryPaneController.reset();
        // Force set initial state
        await this._updateState(() => ({ ...initialState }));
    }

    setIsConstrained(isConstrained: boolean) {
        app.isConstrained = isConstrained;
        return this._updateState({ isConstrained });
    }

    setExternalActivePanel(panel: string) {
        // De-select any selected element when openinng a panel
        if (panel) ds.selection.elment = null;
        this._updateState({ externalActivePanel: panel });
    }

    async setView(view: typeof AppView[keyof typeof AppView]) {
        await this.initializePromise;

        await this._updateState({ view });

        await this._handleTour();
    }

    gotoMarketingPage() {
        window.location.href = "/?";
    }

    private async _handleTour() {
        if (!this.initialized) {
            return;
        }

        const { view } = this._state;

        if (window.baiSource === "collab:view" || window.baiSource === "secureshare") {
            // Landed on library from collab view invite or a secure share
            return;
        }

        const hasTakenTour = !!this.user.get("hasTakenTour");
        if (hasTakenTour) {
            // Already has taken tour
            return;
        }

        const setHasTakenTour = async () => {
            this.user.update({ hasTakenTour: true });
            await this.user.updatePromise;
        };

        if (view !== AppView.LIBRARY) {
            // The following tours are library-specific
            return;
        }

        const { plan } = this.workspaceMetadata;

        if (plan.tier === WorkspacePlanTier.BASIC || plan.type === WorkspacePlanType.PRO_EDU) {
            // Not a pro user, tours are only shown for pro users
            return;
        }

        // The following code is to be executed only once, so mark the user as having taken the tour
        await setHasTakenTour();
    }

    showLibrary({ filter }: { filter?: PresentationLibraryFilter } = {}) {
        let path = "/library";
        if (filter) {
            if (filter.type == "folder") {
                path += `/folder/${filter.folderId}`;
                if (filter.subFolderId) {
                    path += `/${filter.subFolderId}`;
                }
            } else {
                path += "/" + filter.type;
            }
        }

        browserHistory.push(path);
    }

    showTeamResources({ pane, folderId }: { pane?: string, folderId?: string } = {}) {
        let path = `/team-resources/${pane || ""}`;
        // only add the folderId if it's a valid pane
        if (folderId && pane) {
            path += `/${folderId}`;
        }
        browserHistory.push(path);
    }

    showDiscoverTeamPlan() {
        browserHistory.push("/discover-team-plan");
    }

    showAccountPane({ pane }: { pane?: string } = {}) {
        let url = `/account/${pane || ""}`;
        let presentationId = PresentationEditorController.presentation?.id;
        if (presentationId) {
            let slideIndex = PresentationEditorController.getCurrentSlideIndex() + 1;
            url += `?returnId=${presentationId}&slideIndex=${slideIndex}`;
        }
        browserHistory.push(url);
    }

    showPresentationSettings(presentationId: string, pane: string, scrollToSection?: string) {
        const hash = scrollToSection ? `#${scrollToSection}` : "";
        browserHistory.push(`/${presentationId}/settings/${pane}${hash}`);
    }

    showEditor({
        presentationId,
        slideIndex = 1,
        openInNewTab = false,
        presentation = null,
        folderId = null
    }) {
        let url = `/${presentationId}/${slideIndex}`;

        const queryParams = new URLSearchParams();

        if (openInNewTab) {
            window.open(`${url}?${queryParams.toString()}`, "_blank");
        } else {
            if (presentation) {
                // Mount the presentation model to the controller so it can be reused when the editor is initialized
                PresentationEditorController.preloadedPresentation = presentation;
            }
            // Remember the library filter the presentation was opened from so the editor can return to it
            const libraryFilter = browserHistory.location.pathname.startsWith("/library/")
                ? PresentationLibraryPaneController.libraryFilter
                : null;
            browserHistory.push({ pathname: `${url}?${queryParams.toString()}`, state: { folderId, libraryFilter } });
        }
    }

    showSlideEditor({ slideId = null, resourceType, folderId }: { slideId?: string; resourceType: string; folderId?: string }) {
        const pathname = slideId
            ? `/slide/${resourceType}/${slideId}`
            : `/slide/${resourceType}`;

        browserHistory.push({
            pathname,
            state: {
                returnPath: browserHistory.location.pathname,
                ...(folderId ? { folderId } : {}),
            }
        });
    }

    goToRoute(pathname: string, state: any = null) {
        browserHistory.push({
            pathname,
            state
        });
    }

    showCreatePresentationDialog({
        pane,
        search = "",
        isTeamTemplate,
        libraryFilter,
        returnPath = browserHistory.location.pathname,
        returnState = _.cloneDeep(browserHistory.location.state),
        folderId = null,
        hideSidebar = false,
    }: {
        pane?: string;
        search?: string;
        isTeamTemplate?: boolean;
        libraryFilter?: PresentationLibraryFilter;
        returnPath?: string;
        returnState?: any;
        folderId?: string;
        hideSidebar?: boolean;
    }) {
        browserHistory.push({
            pathname: `/create-presentation/${pane ?? ""}`,
            search,
            state: {
                isTeamTemplate,
                libraryFilter,
                returnPath,
                returnState,
                folderId,
                hideSidebar,
            }
        });
    }

    showThemeEditor({
        themeId,
        presentationId,
        pane = "colors",
        subPane,
        returnPath = browserHistory.location.pathname,
        returnState = _.cloneDeep(browserHistory.location.state)
    }: {
        themeId?: string;
        presentationId?: string;
        pane?: string;
        subPane?: string;
        returnPath?: string;
        returnState?: any;
    }) {
        if (presentationId) {
            browserHistory.push({
                pathname: `/${presentationId}/theme/${pane}${subPane ? `/${subPane}` : ""}`,
                state: {
                    returnPath,
                    returnState
                }
            });
            return;
        }

        browserHistory.push({
            pathname: `/theme/${themeId}/${pane}${subPane ? `/${subPane}` : ""}`,
            state: {
                returnPath,
                returnState
            }
        });
    }

    playPresentation({ presentationId, slideIndex = 0 }, state = {}) {
        ShowPlayerLoadingScreen();
        browserHistory.push(`/player/${presentationId}/${slideIndex + 1}`, state);
    }

    showDocumentEditor({ presentationId, openInNewTab }) {
        const url = `/document/${presentationId}`;

        if (openInNewTab) {
            window.open(url, "_blank");
        } else {
            browserHistory.push(url);
        }
    }

    async initializeUser(firebaseUser) {
        if (this.user) {
            // Dispose all existing subscriptions
            this.user.off();
        }

        await this._updateState({ firebaseUser });

        // create the user
        const user = new User({ id: this.firebaseUser.uid }, { autoLoad: false });

        // legacy
        app.user = user;

        // initialize datatservices
        await ds.setup();

        // load the User model
        await user.load();

        const isAccountVerified = this.firebaseUser.emailVerified;
        // check for email verification
        if (!isAccountVerified) {
            // wait for verification
            user.waitForVerifiedEmail().then(() => {
                this._updateState({ isAccountVerified: true });
            });
        }

        await this._updateState({
            user,
            isAccountVerified
        });

        this.user.on("workspacesMetadata:updated", this._handleWorkspacesMetadataUpdated);

        const createForcedRemovalDialogOptions = (teamName: string) => ({
            title: (
                <Fragment>
                    You've been removed from
                    <span style={{ color: "#159bcd", fontWeight: 900 }}>{teamName}</span>
                </ Fragment >
            ),
            message: "You no longer have access to any presentations or shared resources within that workspace",
            buttonText: "Got It",
            buttonOptions: { color: "blue" },
            onClick: () => { }
        });

        if (this.user.has("forcedWorkspaceRemoval")) {
            const options = createForcedRemovalDialogOptions(this.user.get("forcedWorkspaceRemoval"));
            ShowMessageDialog(options);
            this.user.update({ forcedWorkspaceRemoval: null });
        }

        this.user.on("change:forcedWorkspaceRemoval", model => {
            if (model && model.changed && !!model.changed.forcedWorkspaceRemoval) {
                const options = createForcedRemovalDialogOptions(model.changed.forcedWorkspaceRemoval);
                options.onClick = () => window.location.reload();
                ShowMessageDialog(options);
                this.user.update({ forcedWorkspaceRemoval: null });
            }
        });

        this.user.on("change:workspaceDeleted", model => {
            if (model && model.changed && !!model.changed.workspaceDeleted) {
                ShowDialog(MandatoryUpdateDialog, {
                    message: `This browser tab will need to be refreshed since the ${model.changed.workspaceDeleted} workspace was deleted.`
                });
                this.user.update({ workspaceDeleted: null });
            }
        });
    }

    _markNotificationAsRead(notificationId: string) {
        this.notificationsService.markAsRead(notificationId)
            .catch(err => logger.error(err, "[AppController] notificationsService.markAsRead() failed", { id: notificationId }));
    }

    _handleInvitedToWorkspaceNotification(notification) {
        if (notification.event.invitedToWorkspaceId) {
            ShowDialog(MemberAddedToWorkspaceDialog, { notification });
        } else {
            this._markNotificationAsRead(notification.id);
        }
    }

    async _handleTeamTrialUnusedSeatsNotification(notification) {
        // hideFromList reminders never enter notificationsService.unread — track here.
        // Mark any previously received reminder as read so we don't stack dialogs.
        if (this._pendingUnusedSeatsNotification?.id && this._pendingUnusedSeatsNotification.id !== notification.id) {
            this._markNotificationAsRead(this._pendingUnusedSeatsNotification.id);
        }
        this._pendingUnusedSeatsNotification = notification;

        // Debounce: on login, all unread hideFromList notifs fire onNewUnread at once;
        // wait for the batch so we only open dialog for the newest.
        clearTimeout(this._unusedSeatsShowTimeout);
        this._unusedSeatsShowTimeout = setTimeout(async () => {
            const toShow = this._pendingUnusedSeatsNotification;
            if (!toShow) {
                return;
            }

            // Don't stack overlays if AddTeamDialog is already open (leave unread for later).
            if (this._addTeamDialog) {
                return;
            }

            // Dynamic import avoids a circular eval: this file -> AddTeamDialog -> WorkspaceController -> this file
            const { default: AddTeamDialog } = await import(/* webpackMode: "eager" */ "js/editor/PresentationLibrary/components/AddTeamDialog");
            if (this._addTeamDialog) {
                return;
            }
            this._addTeamDialog = ShowDialog(AddTeamDialog, {
                notification: toShow,
                onClose: () => {
                    this._addTeamDialog = null;
                }
            });
        }, 600);
    }

    initializeNotificationsService() {
        if (this.notificationsService) {
            this.notificationsService.dispose();
        }

        clearTimeout(this._unusedSeatsShowTimeout);
        this._pendingUnusedSeatsNotification = null;
        this._addTeamDialog = null;

        this.notificationsService = new NotificationsService(this.user.get("id"));

        // Store the user's modifiedAt value before any modifications happen
        //   so we have a benchmark for the last time they were logged in
        const userModifiedAt = this.user.get("modifiedAt");
        // Subscribe to old notifications
        // This function gets called every time the `unread` array is changed. Since the array is always
        //   updated one item at a time, we debounce so we only process the complete result.
        const onUnreadLoaded = _.debounce(() => {
            // don't interrupt presentations
            if (this.view === AppView.PLAYER) {
                return;
            }

            // Notify the user of any unread team invites
            const unread = this.notificationsService.unread
                .filter(x => x.event.createdAt > userModifiedAt)
                .sort((a, b) => b.event.createdAt - a.event.createdAt);

            if (unread.length) {
                unread.forEach(notification => {
                    if (notification.eventType === EventType.TEAM && notification.event.action === EventAction.INVITED_TO_TEAM && !app.isConstrained) {
                        this._handleInvitedToWorkspaceNotification(notification);
                    }
                });
                // Update the user's `modifiedAt` field so we don't spam the user on next login
                this.user.update({ modifiedAt: Date.now() });
            }

            // Unsubscribe now that we've processed the loaded array of unread notifications
            this.notificationsService.offUnreadChanged(onUnreadLoaded);
        }, 500);
        this.notificationsService.onUnreadChanged(onUnreadLoaded);

        // Subscribe to new notifications
        this.notificationsService.onNewUnread(notification => {
            // Don't interrupt presentations
            if (this.view === AppView.PLAYER || app.isConstrained) {
                return;
            }
            // Ensuring backwards compatibility, assuming notifyWithPopup == true unless it's
            // explicitly set to false
            const notifyWithPopup = this.user.get("notifyWithPopup") === false ? false : true;

            if (notification.eventType === EventType.TEAM) {
                switch (notification.event.action) {
                    case EventAction.INVITED_TO_TEAM:
                        this._handleInvitedToWorkspaceNotification(notification);
                        break;
                    case EventAction.REMIND_TEAM_TRIAL_UNUSED_SEATS:
                        this._handleTeamTrialUnusedSeatsNotification(notification);
                        break;
                }
            } else if (notifyWithPopup) {
                ShowNotificationAlert(notification);
            }
        });
    }

    async setInitialWorkspaceId(initialWorkspaceId?: string) {
        let workspaceId: string = initialWorkspaceId;

        if (workspaceId) {
            setPersistentWorkspaceId(workspaceId);
        }

        if (!workspaceId) {
            workspaceId = getInitialWorkspaceId(this.user.workspacesMetadata, this.user.get("recentWorkspaceId"));
        }

        await this._updateState({ workspaceId });
    }

    _stateDidUpdate(prevState: AppControllerState) {
        const { workspaceId, user, firebaseUser } = this._state;

        if (prevState.workspaceId !== workspaceId || prevState.firebaseUser !== firebaseUser || prevState.user !== user) {
            if (!workspaceId || !firebaseUser || !user) {
                WorkspaceController.reset();
                return;
            }

            WorkspaceController.initialize(workspaceId, firebaseUser, user);
        }
    }

    async switchWorkspace(workspaceId: string, goToLibrary: boolean = true) {
        // Always show the library when switching workspaces unless specified otherwise
        if (goToLibrary) {
            this.showLibrary({ filter: { type: PresentationLibraryFilterType.ALL_PRESENTATIONS, folderId: null, subFolderId: null } });
        }

        setPersistentWorkspaceId(workspaceId);
        await this._updateState({ workspaceId });
    }

    async createWorkspace() {
        const name = await ShowInputDialog({
            title: t("Create Workspace"),
            maxChars: 50,
            value: `${this.firebaseUser.displayName || this.firebaseUser.email?.split("@")[0] || "User"}'s New Workspace`
        });
        if (!name) {
            return false;
        }

        return await withProgressDialog({
            title: t("Creating workspace..."),
            message: t("Please wait while we create your new workspace..."),
            action: async () => {
                try {
                    // This will also reload workspaces metadata and switch to the new workspace
                    await createWorkspace({ name });

                    // @ts-ignore
                    const { default: ChangePlanDialog } = await import(/* webpackMode: "eager" */ "js/react/components/Dialogs/ChangePlanDialog");
                    ShowDialog(ChangePlanDialog, {
                        featuredPlan: WorkspacePlanTier.TEAM,
                        analytics: { cta: "createWorkspace" },
                        title: t("Choose a plan for your new workspace")
                    });

                    return true;
                } catch (err) {
                    logger.error(err, "[AppController] createWorkspace() failed");

                    ShowErrorDialog({
                        title: t("Failed to create workspace"),
                        message: t("Please try again. If the issue persists, please reach out to support@beautiful.ai.")
                    });

                    return false;
                }
            }
        });
    }

    async showDebugInfo() {
        // @ts-ignore
        const { DebugInfoDialog } = await import(/* webpackMode: "eager" */ "js/react/components/Dialogs/DebugInfoDialog");
        ShowDialog(DebugInfoDialog);
    }

    async showInternalBugReport() {
        // @ts-ignore
        const { InternalBugReportDialog } = await import(/* webpackMode: "eager" */ "js/react/components/Dialogs/InternalBugReportDialog");
        ShowDialog(InternalBugReportDialog);
    }
}

// this is for backwards compatibilty with all the code that references app.mainView.editorView
if (!app.mainView) {
    app.mainView = {
        editorView: null
    };
}

const appController = new AppController(initialState);

export default appController;

// DEV-START
if (!global.jestTestActive) {
    // @ts-ignore
    if (import.meta.webpackHot) {
        // @ts-ignore Force refresh
        import.meta.webpackHot.decline();
    }
}
// DEV-END
